import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { MailNav } from "./mail-nav";
import { MailList } from "./mail-list";
import { MailDisplay } from "./mail-display";
import { MailCompose } from "./mail-compose";
import { MailSearch } from "./mail-search";
import { useMail, groupEmailsIntoThreads } from "./use-mail";
import { Mail as MailType } from "./types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PanelLeftClose, PanelLeft, RefreshCw, CheckSquare, X, UserPlus } from "lucide-react";
import { useKeyboardShortcuts } from "./use-keyboard-shortcuts";
import { KeyboardShortcutsDialog } from "./keyboard-shortcuts-dialog";
import { BulkParseDialog } from "./BulkParseDialog";

interface MailProps {
  defaultLayout?: number[];
  defaultCollapsed?: boolean;
  navCollapsedSize?: number;
}

export function Mail({
  defaultLayout = [15, 30, 55],
  defaultCollapsed = false,
  navCollapsedSize = 4,
}: MailProps) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [composeOpen, setComposeOpen] = useState(false);
  const [bulkParseOpen, setBulkParseOpen] = useState(false);
  const [replyContext, setReplyContext] = useState<{
    email: string;
    subject: string;
    messageId?: string;
  } | null>(null);

  const { 
    selectedFolder, 
    selectedMail, 
    setSelectedMail, 
    selectedThread,
    setSelectedThread,
    searchQuery,
    isMultiSelectMode,
    setMultiSelectMode,
    selectedEmailIds,
    clearSelection,
    selectAllEmails,
  } = useMail();
  const queryClient = useQueryClient();

  // Fetch inbound emails with attachments
  const { data: inboundEmails = [], isLoading: inboundLoading, refetch: refetchInbound } = useQuery({
    queryKey: ["mail", "inbound", selectedFolder],
    queryFn: async () => {
      if (selectedFolder === "sent") return [];

      let query = supabase
        .from("inbound_emails")
        .select(`
          *,
          candidates (id, first_name, last_name),
          inbound_email_attachments (id, file_name, file_size, file_type, storage_path, content_id)
        `)
        .order("received_at", { ascending: false });

      if (selectedFolder === "inbox") {
        query = query.eq("is_archived", false);
      } else if (selectedFolder === "archive") {
        query = query.eq("is_archived", true);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []).map((email) => ({
        ...email,
        direction: "inbound" as const,
      })) as MailType[];
    },
    enabled: selectedFolder !== "sent",
  });

  // Fetch sent emails from communications table
  const { data: sentEmails = [], isLoading: sentLoading, refetch: refetchSent } = useQuery({
    queryKey: ["mail", "sent"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("communications")
        .select(`
          id,
          subject,
          body,
          recipient_email,
          sender_email,
          sent_at,
          created_at,
          channel,
          direction,
          status,
          candidate_id,
          message_id,
          in_reply_to,
          candidates (id, first_name, last_name)
        `)
        .eq("channel", "email")
        .eq("direction", "outbound")
        .in("status", ["sent", "delivered"])
        .order("sent_at", { ascending: false })
        .limit(100);

      if (error) throw error;
      
      // Transform to Mail format
      return (data || []).map((comm): MailType => ({
        id: comm.id,
        from_email: comm.sender_email || "",
        from_name: null,
        to_email: comm.recipient_email || "",
        subject: comm.subject,
        body_text: comm.body?.replace(/<[^>]*>/g, "") || null,
        body_html: comm.body,
        received_at: comm.sent_at || comm.created_at,
        sent_at: comm.sent_at,
        is_read: true,
        is_archived: false,
        candidate_id: comm.candidate_id,
        communication_id: comm.id,
        message_id: comm.message_id,
        in_reply_to: comm.in_reply_to,
        direction: "outbound",
        recipient_email: comm.recipient_email,
        sender_email: comm.sender_email,
        candidates: Array.isArray(comm.candidates) ? comm.candidates[0] : comm.candidates,
      }));
    },
    enabled: selectedFolder === "sent",
  });

  // Combine emails based on folder
  const emails = selectedFolder === "sent" ? sentEmails : inboundEmails;
  const isLoading = selectedFolder === "sent" ? sentLoading : inboundLoading;

  const refetch = () => {
    if (selectedFolder === "sent") {
      refetchSent();
    } else {
      refetchInbound();
    }
  };

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (emailId: string) => {
      const { error } = await supabase
        .from("inbound_emails")
        .update({ is_read: true })
        .eq("id", emailId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mail"] });
    },
  });

  // Archive mutation
  const archiveMutation = useMutation({
    mutationFn: async (emailId: string) => {
      const { error } = await supabase
        .from("inbound_emails")
        .update({ is_archived: true })
        .eq("id", emailId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mail"] });
      setSelectedMail(null);
      setSelectedThread(null);
    },
  });

  // Mark selected mail as read when viewed
  useEffect(() => {
    if (selectedMail && !selectedMail.is_read && selectedMail.direction !== "outbound") {
      markAsReadMutation.mutate(selectedMail.id);
    }
  }, [selectedMail?.id]);

  // Filter emails based on search
  const filteredEmails = useMemo(() => {
    if (!searchQuery) return emails;
    const query = searchQuery.toLowerCase();
    return emails.filter(
      (email) =>
        email.subject?.toLowerCase().includes(query) ||
        email.from_email?.toLowerCase().includes(query) ||
        email.from_name?.toLowerCase().includes(query) ||
        email.body_text?.toLowerCase().includes(query) ||
        email.recipient_email?.toLowerCase().includes(query)
    );
  }, [emails, searchQuery]);

  // Group into threads
  const threads = useMemo(() => {
    return groupEmailsIntoThreads(filteredEmails);
  }, [filteredEmails]);

  const unreadCount = inboundEmails.filter((e) => !e.is_read).length;
  const isSentFolder = selectedFolder === "sent";

  // Get selected emails for bulk actions
  const selectedEmails = useMemo(() => {
    return filteredEmails.filter((email) => selectedEmailIds.has(email.id));
  }, [filteredEmails, selectedEmailIds]);

  const handleSelectAll = () => {
    selectAllEmails(filteredEmails.map((e) => e.id));
  };

  const handleCompose = () => {
    setReplyContext(null);
    setComposeOpen(true);
  };

  const handleReply = (mail: MailType) => {
    const emailMatch = mail.from_email?.match(/<(.+)>$/);
    const recipientEmail = mail.direction === "outbound" 
      ? mail.recipient_email || mail.to_email
      : emailMatch ? emailMatch[1] : mail.from_email;

    setReplyContext({
      email: recipientEmail || "",
      subject: mail.subject || "",
      messageId: mail.message_id || undefined,
    });
    setComposeOpen(true);
  };

  // Keyboard shortcuts
  useKeyboardShortcuts({
    mails: filteredEmails,
    threads,
    selectedMail,
    selectedThread,
    onSelectMail: setSelectedMail,
    onSelectThread: setSelectedThread,
    onReply: handleReply,
    onArchive: (id) => archiveMutation.mutate(id),
    onCompose: handleCompose,
    onRefresh: refetch,
    useThreads: true,
  });

  return (
    <TooltipProvider delayDuration={0}>
      <div className="h-full flex flex-col">
        {/* Top bar with refresh */}
        <div className="flex items-center justify-between p-2 border-b">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsCollapsed(!isCollapsed)}
            >
              {isCollapsed ? (
                <PanelLeft className="h-4 w-4" />
              ) : (
                <PanelLeftClose className="h-4 w-4" />
              )}
            </Button>
            <h1 className="text-lg font-semibold">Mail</h1>
          </div>
          <div className="flex items-center gap-1">
            {!isSentFolder && (
              <Button
                variant={isMultiSelectMode ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setMultiSelectMode(!isMultiSelectMode)}
              >
                <CheckSquare className="h-4 w-4 mr-1" />
                Select
              </Button>
            )}
            <KeyboardShortcutsDialog />
            <Button variant="ghost" size="icon" onClick={refetch}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Bulk actions bar */}
        {isMultiSelectMode && (
          <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/50">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handleSelectAll}>
                Select All
              </Button>
              <Button variant="ghost" size="sm" onClick={clearSelection}>
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
              {selectedEmailIds.size > 0 && (
                <Badge variant="secondary">
                  {selectedEmailIds.size} selected
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => setBulkParseOpen(true)}
                disabled={selectedEmailIds.size === 0}
              >
                <UserPlus className="h-4 w-4 mr-1" />
                Create Candidates
              </Button>
            </div>
          </div>
        )}

        <ResizablePanelGroup
          direction="horizontal"
          className="flex-1"
          onLayout={(sizes: number[]) => {
            document.cookie = `react-resizable-panels:layout:mail=${JSON.stringify(
              sizes
            )}`;
          }}
        >
          {/* Nav Panel */}
          <ResizablePanel
            defaultSize={defaultLayout[0]}
            collapsedSize={navCollapsedSize}
            collapsible
            minSize={10}
            maxSize={20}
            onCollapse={() => setIsCollapsed(true)}
            onExpand={() => setIsCollapsed(false)}
            className={cn(
              isCollapsed &&
                "min-w-[50px] transition-all duration-300 ease-in-out"
            )}
          >
            <MailNav
              unreadCount={unreadCount}
              onCompose={handleCompose}
              isCollapsed={isCollapsed}
            />
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* List Panel */}
          <ResizablePanel defaultSize={defaultLayout[1]} minSize={20}>
            <div className="flex flex-col h-full">
              <MailSearch />
              <MailList 
                mails={filteredEmails} 
                threads={threads}
                isLoading={isLoading} 
                useThreads={true}
                isSentFolder={isSentFolder}
              />
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* Display Panel */}
          <ResizablePanel defaultSize={defaultLayout[2]} minSize={30}>
            <MailDisplay
              mail={selectedMail}
              thread={selectedThread}
              onReply={handleReply}
              onArchive={(id) => archiveMutation.mutate(id)}
              isSentFolder={isSentFolder}
            />
          </ResizablePanel>
        </ResizablePanelGroup>

        <MailCompose
          open={composeOpen}
          onOpenChange={setComposeOpen}
          replyTo={replyContext}
          onSent={refetch}
        />

        <BulkParseDialog
          open={bulkParseOpen}
          onOpenChange={setBulkParseOpen}
          selectedEmails={selectedEmails}
          onComplete={() => {
            clearSelection();
            refetch();
          }}
        />
      </div>
    </TooltipProvider>
  );
}
